import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { WSSidebar } from "../common";
import { WSButton } from "../core/WSButton/WSButton.component";
import { WSLoader } from "../core/WSLoader/WSLoader.component";
import { WSText } from "../core/WSText/WSText.component";
import { useIsMobile } from "../layout";
import { WSElement } from "../WSElement/WSElement.component";
import { WSEmptyState, WSEmptyStateProps } from "../WSEmptyState/WSEmptyState";

import { Checkbox, Option } from "./Option";
import { Search } from "./Search";
import {
  WSSelectOption,
  WSSelectPropsBase,
  WSSelectPropsMultiple,
  WSSelectPropsSingle
} from "./types";
import styles from "./WSSelect.module.scss";

const getOptionKey = (option: WSSelectOption, index: number): string =>
  option.value + option.label + index;

type PropsBase = {
  options: WSSelectPropsBase["options"];
  isListLoading?: WSSelectPropsBase["isListLoading"];
  onReachEnd?: WSSelectPropsBase["onReachMenuEnd"];
  footerText?: WSSelectPropsBase["menuFooterText"];
  footerAction?: WSSelectPropsBase["menuFooterAction"];
  emptyState?: WSSelectPropsBase["emptyState"];
  noTranslate?: WSSelectPropsBase["noTranslate"];

  searchText?: string;
  onSearchChange?: (searchText: string) => void;

  targetRef?: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  selectAllLabel?: string;
  listboxId?: string;
  testIdBase?: string;
};

type PropsSingle = PropsBase &
  Pick<WSSelectPropsSingle, "mode" | "value" | "onChange">;

type PropsMultiple = PropsBase &
  Pick<WSSelectPropsMultiple, "mode" | "value" | "onChange">;

type Props = PropsSingle | PropsMultiple;

const Inner: React.FC<Props> = (props) => {
  const {
    options,
    onClose,
    searchText,
    onSearchChange,
    isListLoading,
    onReachEnd,
    footerText,
    footerAction,
    emptyState,
    selectAllLabel = "Select option(s)",
    listboxId,
    testIdBase,
    noTranslate
  } = props;

  const scopedTestId = (suffix: string) =>
    testIdBase ? `${testIdBase}${suffix}` : `select${suffix}`;

  const isMultiple = props.mode === "multiple";

  const [cursor, setCursor] = useState(
    !isMultiple && props.value
      ? options.findIndex((option) => option.value === props.value)
      : -1
  );
  const menuContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMultiple && props.value) {
      setCursor(options.findIndex((option) => option.value === props.value));
    } else if (!isMultiple) {
      setCursor(-1);
    }
  }, [options, isMultiple, props.value]);

  const optionsContainerRef = useRef<HTMLDivElement>(null);

  const onScroll = useCallback(() => {
    if (!optionsContainerRef.current) {
      return;
    }
    if (
      optionsContainerRef.current.scrollHeight ===
      optionsContainerRef.current.scrollTop +
        optionsContainerRef.current.offsetHeight
    ) {
      onReachEnd?.();
    }
  }, [onReachEnd]);

  useEffect(() => {
    onScroll();

    const node = optionsContainerRef.current;
    node?.addEventListener("scroll", onScroll);

    return () => {
      node?.removeEventListener("scroll", onScroll);
    };
  }, [onScroll]);

  const selectedValues = useMemo((): ReadonlyArray<string> => {
    if (!isMultiple) return [];
    return props.value ?? [];
  }, [isMultiple, props.value]);

  const allVisibleSelected = useMemo(
    () =>
      isMultiple &&
      options.length > 0 &&
      options.every((opt) => selectedValues.includes(opt.value)),
    [isMultiple, options, selectedValues]
  );

  const someVisibleSelected = useMemo(
    () =>
      isMultiple &&
      options.some((opt) => selectedValues.includes(opt.value)) &&
      !allVisibleSelected,
    [isMultiple, options, selectedValues, allVisibleSelected]
  );

  const handleSelectAll = useCallback(() => {
    if (props.mode !== "multiple") return;

    if (allVisibleSelected) {
      const visibleValues = new Set(options.map((opt) => opt.value));
      const remaining = (props.value ?? []).filter(
        (v) => !visibleValues.has(v)
      );
      props.onChange(remaining.length > 0 ? remaining : null);
    } else {
      const visibleValues = options.map((opt) => opt.value);
      const existingSet = new Set(props.value ?? []);
      const merged = [
        ...(props.value ?? []),
        ...visibleValues.filter((v) => !existingSet.has(v))
      ];
      props.onChange(merged);
    }
  }, [allVisibleSelected, options, props]);

  const handleOptionClick = useCallback(
    (option: WSSelectOption) => {
      if (props.mode === "multiple") {
        const currentValues = props.value ?? [];
        const isCurrentlySelected = currentValues.includes(option.value);

        if (isCurrentlySelected) {
          const next = currentValues.filter((v) => v !== option.value);
          props.onChange(next.length > 0 ? next : null);
        } else {
          props.onChange([...currentValues, option.value]);
        }
      } else {
        props.onChange(option.value);
        onClose();
      }
    },
    [props, onClose]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case "ArrowUp": {
          event.stopPropagation();
          event.preventDefault();

          setCursor((prev) => {
            if (prev === -1 || prev === 0) {
              return options.length - 1;
            }

            return prev - 1;
          });

          break;
        }
        case "ArrowDown": {
          event.stopPropagation();
          event.preventDefault();

          setCursor((prev) => {
            if (prev === -1 || prev === options.length - 1) {
              return 0;
            }

            return prev + 1;
          });
          break;
        }
        case "Enter": {
          event.stopPropagation();
          event.preventDefault();

          if (cursor === -1) {
            break;
          }

          handleOptionClick(options[cursor]);
          break;
        }
        default:
          break;
      }
    },
    [cursor, handleOptionClick, options]
  );

  useEffect(() => {
    const wrapper = menuContainerRef.current;
    wrapper?.addEventListener("keydown", handleKeyDown);

    return () => {
      wrapper?.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown, menuContainerRef]);

  const emptyStateProps = useMemo(() => {
    if (options.length > 0 || isListLoading) return;

    return searchText
      ? ({
          className: styles.emptyState,
          type: "search",
          bgColor: "gray",
          title: `No results for "${searchText}"`,
          description:
            "Try adjusting your search to find what you're looking for."
        } as WSEmptyStateProps)
      : emptyState;
  }, [searchText, emptyState, isListLoading, options.length]);

  return (
    <WSElement
      as="div"
      ref={menuContainerRef}
      className={styles.wrapper}
      id={listboxId}
      role="listbox"
      data-testid={scopedTestId("Menu")}
    >
      {onSearchChange && (
        <Search
          text={searchText}
          onChange={onSearchChange}
          testIdBase={testIdBase}
        />
      )}

      {isMultiple && !emptyStateProps && (
        <WSElement
          className={styles.selectAllRow}
          onClick={handleSelectAll}
          data-testid={scopedTestId("SelectAll")}
        >
          <Checkbox
            checked={allVisibleSelected}
            indeterminate={someVisibleSelected}
            testId={scopedTestId("SelectAllCheckbox")}
          />
          <WSText.ParagraphSm color="gray600">
            {selectAllLabel}
          </WSText.ParagraphSm>
        </WSElement>
      )}

      {emptyStateProps ? (
        <WSEmptyState
          {...emptyStateProps}
          data-testid={scopedTestId("EmptyState")}
        />
      ) : (
        <WSElement
          className={styles.options}
          ref={optionsContainerRef}
          data-testid={scopedTestId("Options")}
        >
          {isListLoading ? (
            <WSLoader.Spinner
              size="S"
              my="M"
              data-testid={scopedTestId("Loader")}
            />
          ) : (
            options.map((option, index) => (
              <Option
                key={getOptionKey(option, index)}
                {...option}
                onClick={() => handleOptionClick(option)}
                isActive={cursor === index}
                mode={props.mode}
                isSelected={
                  isMultiple ? selectedValues.includes(option.value) : undefined
                }
                testIdBase={testIdBase}
                noTranslate={noTranslate}
              />
            ))
          )}
        </WSElement>
      )}

      {(footerText || footerAction) && (
        <WSElement
          className={styles.footer}
          data-testid={scopedTestId("Footer")}
        >
          <WSText.ParagraphXs
            color="gray400"
            data-testid={scopedTestId("FooterText")}
          >
            {footerText}
          </WSText.ParagraphXs>
          {footerAction && (
            <WSButton.Link
              size="S"
              type="button"
              data-testid={scopedTestId("FooterAction")}
              {...footerAction}
              onClick={(event) => {
                footerAction.onClick?.(event);
                onClose();
              }}
            />
          )}
        </WSElement>
      )}
    </WSElement>
  );
};

type DropdownProps = {
  children: ReactNode;
  onClose: () => void;
  targetRef?: React.RefObject<HTMLDivElement>;
};

const Dropdown: React.FC<DropdownProps> = ({
  children,
  onClose,
  targetRef
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutSideClick = (event: MouseEvent) => {
      if (
        !targetRef?.current?.contains(event.target as any) &&
        !ref.current?.contains(event.target as any)
      ) {
        onClose();
      }
    };

    window.addEventListener("mousedown", handleOutSideClick);

    return () => {
      window.removeEventListener("mousedown", handleOutSideClick);
    };
  }, [onClose, ref, targetRef]);

  return (
    <WSElement ref={ref} className={styles.dropdown}>
      {children}
    </WSElement>
  );
};

export const Menu: React.FC<Props> = (props) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <WSSidebar
        noCloseIcon
        closeOnEscape
        position="bottom"
        size="AUTO"
        contentElementProps={{
          colorBackground: "white",
          p: "NONE",
          className: styles.sidebarContent
        }}
        onClose={props.onClose}
      >
        <Inner {...props} />
      </WSSidebar>
    );
  }

  return (
    <Dropdown onClose={props.onClose} targetRef={props.targetRef}>
      <Inner {...props} />
    </Dropdown>
  );
};
